import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, Luggage, FileText, Smartphone, ClipboardList,
  ChevronDown, ChevronUp, RotateCcw, CheckCircle2
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Checklist data per destination
const CHECKLIST_DATA = {
  iceland: {
    luggage: {
      title_it: "Cosa Mettere in Valigia",
      title_en: "Packing List",
      items: [
        { id: 'thermal', label_it: 'Intimo termico (strati)', label_en: 'Thermal underwear (layers)' },
        { id: 'waterproof', label_it: 'Giacca impermeabile e antivento', label_en: 'Waterproof windbreaker jacket' },
        { id: 'fleece', label_it: 'Pile o maglione caldo', label_en: 'Fleece or warm sweater' },
        { id: 'boots', label_it: 'Scarpe da trekking impermeabili', label_en: 'Waterproof hiking boots' },
        { id: 'swimsuit', label_it: 'Costume da bagno (per hot springs)', label_en: 'Swimsuit (for hot springs)' },
        { id: 'beanie', label_it: 'Berretto e guanti', label_en: 'Beanie and gloves' },
        { id: 'sunglasses', label_it: 'Occhiali da sole polarizzati', label_en: 'Polarized sunglasses' },
        { id: 'backpack', label_it: 'Zaino impermeabile', label_en: 'Waterproof backpack' },
        { id: 'camera', label_it: 'Fotocamera + batterie extra', label_en: 'Camera + extra batteries' },
        { id: 'tripod', label_it: 'Treppiede (per aurora)', label_en: 'Tripod (for aurora)' },
        { id: 'powerbank', label_it: 'Power bank', label_en: 'Power bank' },
        { id: 'adapter', label_it: 'Adattatore europeo', label_en: 'European adapter' }
      ]
    },
    documents: {
      title_it: "Documenti Necessari",
      title_en: "Required Documents",
      items: [
        { id: 'passport', label_it: 'Passaporto/Carta d\'identità valida', label_en: 'Valid passport/ID card' },
        { id: 'insurance', label_it: 'Assicurazione viaggio', label_en: 'Travel insurance' },
        { id: 'license', label_it: 'Patente internazionale (se noleggi)', label_en: 'International driving license (if renting)' },
        { id: 'booking', label_it: 'Conferme prenotazioni hotel', label_en: 'Hotel booking confirmations' },
        { id: 'flight', label_it: 'Biglietti aerei', label_en: 'Flight tickets' },
        { id: 'cards', label_it: 'Carte di credito (Visa/Mastercard)', label_en: 'Credit cards (Visa/Mastercard)' },
        { id: 'health', label_it: 'Tessera sanitaria europea', label_en: 'European health card' }
      ]
    },
    apps: {
      title_it: "App da Scaricare",
      title_en: "Apps to Download",
      items: [
        { id: 'vedur', label_it: 'Vedur (meteo islandese)', label_en: 'Vedur (Icelandic weather)' },
        { id: 'aurora', label_it: 'Aurora Forecast', label_en: 'Aurora Forecast' },
        { id: 'maps', label_it: 'Maps.me (mappe offline)', label_en: 'Maps.me (offline maps)' },
        { id: 'road', label_it: 'Road.is (condizioni strade)', label_en: 'Road.is (road conditions)' },
        { id: 'safetravel', label_it: 'SafeTravel Iceland', label_en: 'SafeTravel Iceland' },
        { id: 'translate', label_it: 'Google Translate (offline)', label_en: 'Google Translate (offline)' }
      ]
    },
    todo: {
      title_it: "Prima di Partire",
      title_en: "Before Departure",
      items: [
        { id: 'notify_bank', label_it: 'Avvisare la banca del viaggio', label_en: 'Notify bank of travel' },
        { id: 'download_maps', label_it: 'Scaricare mappe offline', label_en: 'Download offline maps' },
        { id: 'check_weather', label_it: 'Controllare previsioni meteo', label_en: 'Check weather forecast' },
        { id: 'car_rental', label_it: 'Confermare noleggio auto', label_en: 'Confirm car rental' },
        { id: 'emergency', label_it: 'Salvare numeri emergenza', label_en: 'Save emergency numbers' },
        { id: 'copy_docs', label_it: 'Fare copie dei documenti', label_en: 'Make copies of documents' }
      ]
    }
  },
  japan: {
    luggage: {
      title_it: "Cosa Mettere in Valigia",
      title_en: "Packing List",
      items: [
        { id: 'comfortable', label_it: 'Scarpe comode (si cammina molto!)', label_en: 'Comfortable shoes (lots of walking!)' },
        { id: 'layers', label_it: 'Vestiti a strati', label_en: 'Layered clothing' },
        { id: 'socks', label_it: 'Calzini puliti extra (templi!)', label_en: 'Extra clean socks (temples!)' },
        { id: 'umbrella', label_it: 'Ombrello pieghevole', label_en: 'Foldable umbrella' },
        { id: 'small_bag', label_it: 'Borsa piccola per escursioni', label_en: 'Small bag for day trips' },
        { id: 'towel', label_it: 'Asciugamano piccolo', label_en: 'Small towel' },
        { id: 'mask', label_it: 'Mascherine (rispetto locale)', label_en: 'Face masks (local respect)' },
        { id: 'camera', label_it: 'Fotocamera', label_en: 'Camera' },
        { id: 'adapter_jp', label_it: 'Adattatore tipo A/B', label_en: 'Type A/B adapter' },
        { id: 'powerbank', label_it: 'Power bank', label_en: 'Power bank' },
        { id: 'portable_wifi', label_it: 'Pocket WiFi prenotato', label_en: 'Pocket WiFi booked' }
      ]
    },
    documents: {
      title_it: "Documenti Necessari",
      title_en: "Required Documents",
      items: [
        { id: 'passport', label_it: 'Passaporto (6 mesi validità)', label_en: 'Passport (6 months validity)' },
        { id: 'jrpass', label_it: 'JR Pass (se acquistato)', label_en: 'JR Pass (if purchased)' },
        { id: 'insurance', label_it: 'Assicurazione viaggio', label_en: 'Travel insurance' },
        { id: 'booking', label_it: 'Conferme prenotazioni', label_en: 'Booking confirmations' },
        { id: 'cash', label_it: 'Contanti in Yen (Giappone usa molto cash)', label_en: 'Cash in Yen (Japan uses cash a lot)' },
        { id: 'cards', label_it: 'Carte di credito', label_en: 'Credit cards' },
        { id: 'itinerary', label_it: 'Itinerario stampato', label_en: 'Printed itinerary' }
      ]
    },
    apps: {
      title_it: "App da Scaricare",
      title_en: "Apps to Download",
      items: [
        { id: 'hyperdia', label_it: 'Hyperdia/Navitime (treni)', label_en: 'Hyperdia/Navitime (trains)' },
        { id: 'google_maps', label_it: 'Google Maps (offline Tokyo, Kyoto)', label_en: 'Google Maps (offline Tokyo, Kyoto)' },
        { id: 'translate', label_it: 'Google Translate + fotocamera', label_en: 'Google Translate + camera' },
        { id: 'tabelog', label_it: 'Tabelog (ristoranti)', label_en: 'Tabelog (restaurants)' },
        { id: 'suica', label_it: 'Suica/Pasmo (trasporti)', label_en: 'Suica/Pasmo (transport)' },
        { id: 'paypal', label_it: 'PayPay (pagamenti)', label_en: 'PayPay (payments)' }
      ]
    },
    todo: {
      title_it: "Prima di Partire",
      title_en: "Before Departure",
      items: [
        { id: 'jrpass_order', label_it: 'Ordinare JR Pass online', label_en: 'Order JR Pass online' },
        { id: 'wifi_book', label_it: 'Prenotare Pocket WiFi', label_en: 'Book Pocket WiFi' },
        { id: 'yen', label_it: 'Cambiare Yen prima di partire', label_en: 'Exchange Yen before departure' },
        { id: 'temples', label_it: 'Prenotare templi famosi (Kinkaku-ji etc)', label_en: 'Book famous temples (Kinkaku-ji etc)' },
        { id: 'restaurant', label_it: 'Prenotare ristoranti speciali', label_en: 'Book special restaurants' },
        { id: 'phrases', label_it: 'Imparare frasi base giapponesi', label_en: 'Learn basic Japanese phrases' }
      ]
    }
  },
  new_zealand: {
    luggage: {
      title_it: "Cosa Mettere in Valigia",
      title_en: "Packing List",
      items: [
        { id: 'layers', label_it: 'Vestiti a strati (4 stagioni in 1 giorno!)', label_en: 'Layered clothing (4 seasons in 1 day!)' },
        { id: 'rain', label_it: 'Giacca antipioggia', label_en: 'Rain jacket' },
        { id: 'hiking', label_it: 'Scarpe da trekking', label_en: 'Hiking boots' },
        { id: 'sandals', label_it: 'Sandali per le spiagge', label_en: 'Sandals for beaches' },
        { id: 'sunscreen', label_it: 'Crema solare SPF50+', label_en: 'Sunscreen SPF50+' },
        { id: 'sunglasses', label_it: 'Occhiali da sole', label_en: 'Sunglasses' },
        { id: 'insect', label_it: 'Repellente insetti (sandflies!)', label_en: 'Insect repellent (sandflies!)' },
        { id: 'camera', label_it: 'Action cam/Fotocamera', label_en: 'Action cam/Camera' },
        { id: 'adapter_nz', label_it: 'Adattatore tipo I', label_en: 'Type I adapter' },
        { id: 'sleeping', label_it: 'Sacco a pelo (se campeggi)', label_en: 'Sleeping bag (if camping)' }
      ]
    },
    documents: {
      title_it: "Documenti Necessari",
      title_en: "Required Documents",
      items: [
        { id: 'passport', label_it: 'Passaporto (3 mesi validità)', label_en: 'Passport (3 months validity)' },
        { id: 'nzeta', label_it: 'NZeTA (visto elettronico)', label_en: 'NZeTA (electronic visa)' },
        { id: 'ivl', label_it: 'IVL (tassa turistica)', label_en: 'IVL (tourist levy)' },
        { id: 'license', label_it: 'Patente internazionale', label_en: 'International driving license' },
        { id: 'insurance', label_it: 'Assicurazione viaggio completa', label_en: 'Comprehensive travel insurance' },
        { id: 'camper', label_it: 'Prenotazione camper/auto', label_en: 'Camper/car booking' },
        { id: 'cards', label_it: 'Carte di credito', label_en: 'Credit cards' }
      ]
    },
    apps: {
      title_it: "App da Scaricare",
      title_en: "Apps to Download",
      items: [
        { id: 'campermate', label_it: 'CamperMate (campeggi)', label_en: 'CamperMate (campsites)' },
        { id: 'rankers', label_it: 'Rankers Camping NZ', label_en: 'Rankers Camping NZ' },
        { id: 'metservice', label_it: 'MetService (meteo)', label_en: 'MetService (weather)' },
        { id: 'doc', label_it: 'DOC Camping (campeggi gov)', label_en: 'DOC Camping (gov campsites)' },
        { id: 'maps', label_it: 'Maps.me offline', label_en: 'Maps.me offline' },
        { id: 'gaspy', label_it: 'Gaspy (prezzi benzina)', label_en: 'Gaspy (fuel prices)' }
      ]
    },
    todo: {
      title_it: "Prima di Partire",
      title_en: "Before Departure",
      items: [
        { id: 'nzeta_apply', label_it: 'Richiedere NZeTA (72h prima)', label_en: 'Apply for NZeTA (72h before)' },
        { id: 'ivl_pay', label_it: 'Pagare IVL', label_en: 'Pay IVL' },
        { id: 'great_walks', label_it: 'Prenotare Great Walks (mesi prima!)', label_en: 'Book Great Walks (months ahead!)' },
        { id: 'camper_book', label_it: 'Prenotare camper', label_en: 'Book camper' },
        { id: 'milford', label_it: 'Prenotare crociera Milford Sound', label_en: 'Book Milford Sound cruise' },
        { id: 'seasons', label_it: 'Verificare stagione (invertita!)', label_en: 'Check season (reversed!)' }
      ]
    }
  },
  sicily: {
    luggage: {
      title_it: "Cosa Mettere in Valigia",
      title_en: "Packing List",
      items: [
        { id: 'light', label_it: 'Vestiti leggeri e traspiranti', label_en: 'Light breathable clothing' },
        { id: 'swimsuit', label_it: 'Costume da bagno', label_en: 'Swimsuit' },
        { id: 'hat', label_it: 'Cappello per il sole', label_en: 'Sun hat' },
        { id: 'sunscreen', label_it: 'Crema solare', label_en: 'Sunscreen' },
        { id: 'walking', label_it: 'Scarpe comode per passeggiare', label_en: 'Comfortable walking shoes' },
        { id: 'sandals', label_it: 'Sandali', label_en: 'Sandals' },
        { id: 'cover', label_it: 'Copri spalle per chiese', label_en: 'Shoulder cover for churches' },
        { id: 'evening', label_it: 'Outfit elegante per cena', label_en: 'Smart outfit for dinner' },
        { id: 'camera', label_it: 'Fotocamera', label_en: 'Camera' },
        { id: 'adapter', label_it: 'Adattatore europeo (se necessario)', label_en: 'European adapter (if needed)' }
      ]
    },
    documents: {
      title_it: "Documenti Necessari",
      title_en: "Required Documents",
      items: [
        { id: 'id', label_it: 'Carta d\'identità/Passaporto', label_en: 'ID card/Passport' },
        { id: 'health', label_it: 'Tessera sanitaria', label_en: 'Health card' },
        { id: 'license', label_it: 'Patente di guida', label_en: 'Driving license' },
        { id: 'booking', label_it: 'Conferme prenotazioni', label_en: 'Booking confirmations' },
        { id: 'car', label_it: 'Prenotazione auto (consigliata!)', label_en: 'Car rental booking (recommended!)' },
        { id: 'cards', label_it: 'Carte di credito + contanti', label_en: 'Credit cards + cash' }
      ]
    },
    apps: {
      title_it: "App da Scaricare",
      title_en: "Apps to Download",
      items: [
        { id: 'google_maps', label_it: 'Google Maps', label_en: 'Google Maps' },
        { id: 'tripadvisor', label_it: 'TripAdvisor', label_en: 'TripAdvisor' },
        { id: 'thefork', label_it: 'TheFork (ristoranti)', label_en: 'TheFork (restaurants)' },
        { id: 'moovit', label_it: 'Moovit (trasporti)', label_en: 'Moovit (transport)' },
        { id: 'booking', label_it: 'Booking.com', label_en: 'Booking.com' }
      ]
    },
    todo: {
      title_it: "Prima di Partire",
      title_en: "Before Departure",
      items: [
        { id: 'car_book', label_it: 'Prenotare auto a noleggio', label_en: 'Book rental car' },
        { id: 'etna', label_it: 'Prenotare escursione Etna', label_en: 'Book Etna excursion' },
        { id: 'restaurant', label_it: 'Prenotare ristoranti famosi', label_en: 'Book famous restaurants' },
        { id: 'beaches', label_it: 'Ricercare spiagge (lidi vs libere)', label_en: 'Research beaches (private vs public)' },
        { id: 'siesta', label_it: 'Ricordare orari siesta (13-16)', label_en: 'Remember siesta hours (1-4pm)' },
        { id: 'cash', label_it: 'Portare contanti (piccoli locali)', label_en: 'Bring cash (small shops)' }
      ]
    }
  }
};

// Map guide UUIDs to checklist keys
const getChecklistKey = (guideTitle) => {
  const titleLower = guideTitle?.toLowerCase() || '';
  if (titleLower.includes('iceland') || titleLower.includes('islanda')) return 'iceland';
  if (titleLower.includes('japan') || titleLower.includes('giappone')) return 'japan';
  if (titleLower.includes('zealand') || titleLower.includes('zelanda')) return 'new_zealand';
  if (titleLower.includes('sicily') || titleLower.includes('sicilia')) return 'sicily';
  return 'iceland'; // default
};

const ChecklistCategory = ({ category, items, checkedItems, onToggle, language }) => {
  const [isOpen, setIsOpen] = useState(true);
  const title = language === 'it' ? category.title_it : category.title_en;
  const completedCount = items.filter(item => checkedItems[item.id]).length;
  const isComplete = completedCount === items.length;

  const categoryIcons = {
    luggage: Luggage,
    documents: FileText,
    apps: Smartphone,
    todo: ClipboardList
  };

  return (
    <div className="bg-[#2C2C2E] rounded-2xl overflow-hidden border border-[#3A3A3C]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#3A3A3C]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isComplete ? 'bg-green-500/20' : 'bg-[#C9B896]/20'
          }`}>
            {isComplete ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <Luggage className="w-5 h-5 text-[#C9B896]" />
            )}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-[#F5F5F7]">{title}</h3>
            <p className="text-sm text-[#86868B]">
              {completedCount}/{items.length} {language === 'it' ? 'completati' : 'completed'}
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#86868B]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#86868B]" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-2">
              {items.map((item) => {
                const label = language === 'it' ? item.label_it : item.label_en;
                const isChecked = checkedItems[item.id] || false;

                return (
                  <button
                    key={item.id}
                    onClick={() => onToggle(item.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isChecked 
                        ? 'bg-green-500/10 border border-green-500/30' 
                        : 'bg-[#1C1C1E] border border-transparent hover:border-[#3A3A3C]'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                      isChecked 
                        ? 'bg-green-500' 
                        : 'bg-[#3A3A3C]'
                    }`}>
                      {isChecked && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <span className={`text-left text-sm ${
                      isChecked 
                        ? 'text-[#86868B] line-through' 
                        : 'text-[#F5F5F7]'
                    }`}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GuideChecklist = ({ guideId, guideTitle }) => {
  const { language } = useLanguage();
  const checklistKey = getChecklistKey(guideTitle);
  const checklistData = CHECKLIST_DATA[checklistKey] || CHECKLIST_DATA.iceland;

  // Load saved state from localStorage
  const storageKey = `huacaya_checklist_${guideId}`;
  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(checkedItems));
  }, [checkedItems, storageKey]);

  const handleToggle = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleReset = () => {
    if (window.confirm(language === 'it' 
      ? 'Sei sicuro di voler resettare la checklist?' 
      : 'Are you sure you want to reset the checklist?'
    )) {
      setCheckedItems({});
    }
  };

  // Calculate overall progress
  const allItems = [
    ...checklistData.luggage.items,
    ...checklistData.documents.items,
    ...checklistData.apps.items,
    ...checklistData.todo.items
  ];
  const totalChecked = allItems.filter(item => checkedItems[item.id]).length;
  const progressPercent = Math.round((totalChecked / allItems.length) * 100);

  const texts = {
    it: {
      title: "La Tua Checklist di Viaggio",
      subtitle: "Spunta gli elementi man mano che prepari il viaggio",
      progress: "Progresso",
      reset: "Resetta",
      complete: "Preparazione completata!"
    },
    en: {
      title: "Your Travel Checklist",
      subtitle: "Check off items as you prepare for your trip",
      progress: "Progress",
      reset: "Reset",
      complete: "Preparation complete!"
    }
  };

  const t = texts[language] || texts.it;

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-['Manrope'] font-semibold text-2xl md:text-3xl text-white mb-1">
            {t.title}
          </h2>
          <p className="text-[#86868B]">{t.subtitle}</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Progress indicator */}
          <div className="flex items-center gap-3">
            <div className="w-32 h-2 bg-[#3A3A3C] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                className={`h-full rounded-full ${
                  progressPercent === 100 ? 'bg-green-500' : 'bg-[#C9B896]'
                }`}
              />
            </div>
            <span className="text-[#F5F5F7] font-medium text-sm">
              {progressPercent}%
            </span>
          </div>

          {/* Reset button */}
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-[#3A3A3C] hover:bg-[#4A4A4C] rounded-xl text-[#86868B] hover:text-[#F5F5F7] transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            {t.reset}
          </button>
        </div>
      </div>

      {/* Completion message */}
      {progressPercent === 100 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 flex items-center gap-3"
        >
          <CheckCircle2 className="w-6 h-6 text-green-500" />
          <span className="text-green-400 font-medium">{t.complete} 🎉</span>
        </motion.div>
      )}

      {/* Checklist Categories */}
      <div className="grid md:grid-cols-2 gap-4">
        <ChecklistCategory
          category={checklistData.luggage}
          items={checklistData.luggage.items}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          language={language}
        />
        <ChecklistCategory
          category={checklistData.documents}
          items={checklistData.documents.items}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          language={language}
        />
        <ChecklistCategory
          category={checklistData.apps}
          items={checklistData.apps.items}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          language={language}
        />
        <ChecklistCategory
          category={checklistData.todo}
          items={checklistData.todo.items}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          language={language}
        />
      </div>
    </div>
  );
};

export default GuideChecklist;
